import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useRouter } from 'src/routes/hooks';

import { CharCapitalize } from 'src/utils/change-case';

import { useAuthContext } from 'src/auth/hooks';

import { varHover } from 'src/components/animate';
import { CustomPopover, usePopover } from 'src/components/custom-popover';
import { paths } from 'src/routes/paths';
import { signOut } from 'src/auth/context/jwt';
import { useTranslate } from 'src/locales';
// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'profile',
    linkTo: paths.profile.root,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const { t: tCommon } = useTranslate('common');
  const router = useRouter();

  const { user, checkUserSession } = useAuthContext();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await signOut();
      await checkUserSession?.();
      popover.onClose();
      router.replace(paths.dashboard.root);
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  return (
    <Box dir="ltr">
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          alt={user?.firstName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {user?.firstName ? CharCapitalize(user?.firstName?.charAt(0)) : ''}
        </Avatar>
      </IconButton>

      <CustomPopover
        anchorEl={popover.anchorEl}
        open={popover.open}
        onClose={popover.onClose}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => handleClickItem(option.linkTo)}
            >
              {tCommon(option.label)}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          {tCommon('logout')}
        </MenuItem>
      </CustomPopover>
    </Box>
  );
}
